/**
 * Which states of a QR number still allow sending from the app.
 *
 * Only a number that cannot carry a message at all -- LOGGED_OUT (a fresh QR scan is required), or never finished pairing (CREATING /
 * QR_PENDING) -- replaces the composer with the "connect it first" prompt. While a number is merely reconnecting (RECONNECTING,
 * AUTHENTICATING, STOPPED, DISCONNECTED) or paused after "connection replaced" (ERROR) the composer stays exactly as it is: what the user
 * sends is queued and goes out when the number is back. Mirrors the server (server/services/whatsapp/qrNumberUsability.js).
 */
const QR_UNUSABLE_STATES = new Set(["LOGGED_OUT", "CREATING", "QR_PENDING"]);

/** True when a number in this state accepts a send (it is queued when the number is not CONNECTED right now). Unknown (no state) = not usable. */
export const isQrNumberUsableState = (state) => !!state && !QR_UNUSABLE_STATES.has(state);

/**
 * True when the workspace has several numbers but this chat's own number could not be found (see useChatQrNumber). Nothing then says which of
 * them the chat is on, so the workspace's first number must not be taken for it.
 */
export const isChatNumberUnknownAmongSeveral = ({ chatNumber, connections } = {}) => !chatNumber && Array.isArray(connections) && connections.length > 1;

/**
 * The state the composer of a QR chat is judged by.
 *  - the chat's own number is known: that number's state;
 *  - unknown among several numbers: the server's rule for a chat that has no number is "the first number that can send", and it refuses only when
 *    none can, so the composer stays open while any number is usable;
 *  - otherwise (one number, or the list is not known): the workspace's first-number state, exactly as before.
 */
export const composerQrState = ({ chatNumber, connections, workspaceState } = {}) => {
  if (chatNumber) return chatNumber.state;
  if (isChatNumberUnknownAmongSeveral({ chatNumber, connections })) {
    const usable = connections.find((c) => isQrNumberUsableState(c?.state));
    if (usable) return usable.state;
  }
  return workspaceState;
};
